import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Clock, BookOpen, CheckCircle, ArrowLeft, ChartLine, CalendarCheck } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { formatDuration, shortDate, tickDate, formatRelative } from "@/lib/analyticsFormat";

/**
 * Shared analytics drill-down view.
 * - When `selfView=true` we fetch `/analytics/me` (employee viewing own stats)
 * - Otherwise we fetch `/analytics/employees/:userId` (admin drill-down)
 */
export default function EmployeeAnalytics({ selfView = false }) {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const path = selfView ? `/analytics/me` : `/analytics/employees/${userId}`;
        const { data } = await api.get(path);
        if (alive) setData(data);
      } catch (e) {
        setError(formatApiError(e, "Could not load stats."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId, selfView]);

  const summary = data?.summary;
  const dailyChart = (data?.daily || []).map((d) => ({
    date: d.date,
    minutes: +(d.seconds / 60).toFixed(1),
  }));

  return (
    <AppLayout
      title={selfView ? "My Learning Stats" : summary?.name || "Employee analytics"}
      eyebrow={selfView ? "Your training progress" : "Drill-down"}
      actions={
        !selfView && (
          <Link
            to="/admin/analytics"
            data-testid="back-to-analytics"
            className="px-3 py-2 text-sm font-medium text-[#5C5C58] hover:text-[#1C1C19] inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} weight="bold" /> Back to analytics
          </Link>
        )
      }
    >
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      {!selfView && summary && (
        <div className="text-sm text-[#5C5C58] mb-6">
          <span className="font-semibold text-[#1C1C19]">{summary.email}</span> · joined {shortDate(summary.created_at)}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Card
          testid="emp-card-total"
          icon={Clock}
          label="Total watch time"
          value={loading ? "—" : formatDuration(summary?.total_watched_seconds || 0)}
        />
        <Card
          testid="emp-card-completed"
          icon={CheckCircle}
          label="Lessons completed"
          value={loading ? "—" : summary?.lessons_completed ?? 0}
        />
        <Card
          testid="emp-card-courses"
          icon={BookOpen}
          label="Courses enrolled"
          value={loading ? "—" : summary?.courses_enrolled ?? 0}
        />
        <Card
          testid="emp-card-active"
          icon={CalendarCheck}
          label="Active days (30d)"
          value={loading ? "—" : summary?.active_days_30d ?? 0}
          sub={summary?.last_login_at ? `Last login ${formatRelative(summary.last_login_at)}` : "Never logged in"}
        />
      </div>

      {/* Daily activity chart */}
      <div className="bg-white border border-[#E5E2DC] p-5 rounded-md mb-8" data-testid="emp-daily-chart">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-heading text-lg font-semibold text-[#1C1C19]">Daily watch time</div>
            <div className="text-xs text-[#5C5C58]">Minutes watched per day · last 30 days</div>
          </div>
          <ChartLine size={20} weight="duotone" className="text-[#E25822]" />
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <AreaChart data={dailyChart} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="empWatchGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E25822" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#E25822" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFECE5" />
              <XAxis dataKey="date" tickFormatter={tickDate} stroke="#5C5C58" fontSize={11} />
              <YAxis stroke="#5C5C58" fontSize={11} />
              <Tooltip
                formatter={(v) => [`${v} min`, "Watched"]}
                labelFormatter={(d) => shortDate(d)}
                contentStyle={{ background: "#1C1C19", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
              />
              <Area type="monotone" dataKey="minutes" stroke="#E25822" strokeWidth={2.5} fill="url(#empWatchGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-course breakdown */}
      <div className="bg-white border border-[#E5E2DC] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E2DC]">
          <div className="font-heading text-lg font-semibold text-[#1C1C19]">Per-course progress</div>
          <div className="text-xs text-[#5C5C58]">{(data?.courses || []).length} enrolled course{(data?.courses || []).length === 1 ? "" : "s"}</div>
        </div>
        {(data?.courses || []).length === 0 ? (
          <div className="px-5 py-10 text-center text-[#8A8A85] text-sm">No course enrollments yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="emp-courses-table">
            <thead className="bg-[#FAF7F2] text-xs uppercase tracking-wider text-[#5C5C58]">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">Course</th>
                <th className="px-5 py-3 text-right font-semibold">Progress</th>
                <th className="px-5 py-3 text-right font-semibold">Lessons</th>
                <th className="px-5 py-3 text-right font-semibold">Watched</th>
              </tr>
            </thead>
            <tbody>
              {(data?.courses || []).map((c) => (
                <tr key={c.course_id} className="border-t border-[#EFECE5]">
                  <td className="px-5 py-3">
                    <Link to={`/courses/${c.course_id}`} className="font-semibold text-[#1C1C19] hover:text-[#E25822]">
                      {c.title}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex flex-col items-end gap-1">
                      <span className="text-[#1C1C19] font-medium">{c.percent}%</span>
                      <div className="w-24 h-1.5 bg-[#EFECE5] rounded-full overflow-hidden">
                        <div className="h-full bg-[#E25822]" style={{ width: `${Math.min(100, c.percent)}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right text-[#5C5C58]">
                    {c.completed_lessons} / {c.total_lessons}
                  </td>
                  <td className="px-5 py-3 text-right text-[#5C5C58]">{formatDuration(c.watched_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}

function Card({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E2DC] p-5 rounded-md">
      <div className="flex items-center justify-between mb-3">
        <Icon size={20} weight="duotone" className="text-[#5C5C58]" />
      </div>
      <div className="font-heading text-3xl font-semibold text-[#1C1C19] mb-1">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#5C5C58] font-medium">{label}</div>
      {sub && <div className="text-[11px] text-[#8A8A85] mt-1">{sub}</div>}
    </div>
  );
}
